import { useState } from 'react';
import { IconButton, Tab, Tabs, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TabHeader from '../../../../components/TabHeader';
import ConfirmDeleteDialog from '../../../../components/ConfirmDeleteDialog';
import type { SettingsCategoryRow } from '../../types';
import type { CategorySpecification } from '../../../planning/types';
import CategoryRow from './CategoryRow';
import CategoryModal from './CategoryModal';
import { useManageCategories } from '../../hooks/useManageCategories';
import { useOptions } from '../../../planning/hooks/useOptions';
import { useNotification } from '../../../../context/useNotification';
import { CATEGORIES_TAB_LABELS as LABELS } from '../../../../utils/labels';
import { hasFullAccess } from '../../../../utils/roles';
import type { MemberRole } from '../../../../types/roles';

interface CategoriesTabProps {
  orgId: string | null;
  isSuperAdmin: boolean;
  role: MemberRole | null;
}

interface CategoryModalState {
  mode: 'create' | 'edit';
  category?: SettingsCategoryRow;
}

export default function CategoriesTab({ orgId, isSuperAdmin, role }: CategoriesTabProps) {
  const canEdit = hasFullAccess(role, isSuperAdmin);
  const {
    categories,
    archivedCategories,
    loadArchived,
    addCategory,
    updateCategory,
    archiveCategory,
    unarchiveCategory,
    deleteCategory,
  } = useManageCategories(orgId);
  const { options, addOption } = useOptions(orgId);
  const { notifySuccess, notifyError } = useNotification();

  const [tab, setTab] = useState<'active' | 'archived'>('active');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [categoryModal, setCategoryModal] = useState<CategoryModalState | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const handleTabChange = (_: React.SyntheticEvent, value: 'active' | 'archived') => {
    setTab(value);
    if (value === 'archived') loadArchived();
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    setDeleteError(null);
    const result = await deleteCategory(id);
    if (result === 'ok') {
      notifySuccess(LABELS.deleteSuccess);
    } else if (result === 'has_machines') {
      setDeleteError(LABELS.deleteErrorHasMachines);
      notifyError(LABELS.deleteErrorHasMachines);
    } else {
      setDeleteError(LABELS.deleteError);
      notifyError(LABELS.deleteError);
    }
  };

  const handleSave = async (name: string, optionIds: string[], specifications: CategorySpecification[]): Promise<boolean> => {
    const ok = categoryModal?.mode === 'create'
      ? await addCategory(name, optionIds, specifications)
      : categoryModal?.category
        ? await updateCategory(categoryModal.category.id, name, optionIds, specifications)
        : false;
    if (ok) notifySuccess(LABELS.saveSuccess); else notifyError(LABELS.mutationError);
    return ok;
  };

  const handleArchive = async (id: string) => {
    const ok = await archiveCategory(id);
    if (ok) notifySuccess(LABELS.archiveSuccess); else notifyError(LABELS.mutationError);
  };

  const handleUnarchive = async (id: string) => {
    const ok = await unarchiveCategory(id);
    if (ok) notifySuccess(LABELS.unarchiveSuccess); else notifyError(LABELS.mutationError);
  };

  const list = tab === 'active' ? categories : archivedCategories;

  return (
    <div className="space-y-6">
      <TabHeader
        title={LABELS.title}
        description={LABELS.subtitle}
        addLabel={canEdit ? LABELS.newButton : undefined}
        onAdd={canEdit ? () => setCategoryModal({ mode: 'create' }) : undefined}
      />

      {deleteError && (
        <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-2 flex items-center gap-2">
          <p className="text-sm text-red-700 flex-1">{deleteError}</p>
          <Tooltip title={LABELS.dismissError}>
            <IconButton size="small" onClick={() => setDeleteError(null)} aria-label={LABELS.dismissError}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      )}

      <div className="rounded-2xl border border-border overflow-hidden bg-surface">
        <Tabs value={tab} onChange={handleTabChange} className="border-b border-border px-2">
          <Tab label={LABELS.tabActive} value="active" />
          <Tab label={LABELS.tabArchived} value="archived" />
        </Tabs>

        {list.length === 0 ? (
          <p className="text-sm text-text-secondary text-center py-6">
            {tab === 'active' ? LABELS.noActive : LABELS.noArchived}
          </p>
        ) : (
          list.map((c, i) => (
            <CategoryRow
              key={c.id}
              category={c}
              options={options}
              showDivider={i > 0}
              onEdit={canEdit ? () => setCategoryModal({ mode: 'edit', category: c }) : undefined}
              onArchive={canEdit && !c.archived ? () => handleArchive(c.id) : undefined}
              onUnarchive={canEdit && c.archived ? () => handleUnarchive(c.id) : undefined}
              onDelete={canEdit ? () => setPendingDeleteId(c.id) : undefined}
            />
          ))
        )}
      </div>

      {categoryModal && (
        <CategoryModal
          category={categoryModal.category}
          options={options}
          existingCategories={[...categories, ...archivedCategories]}
          onAddOption={addOption}
          onClose={() => setCategoryModal(null)}
          onSave={handleSave}
        />
      )}

      <ConfirmDeleteDialog
        open={!!pendingDeleteId}
        onClose={() => setPendingDeleteId(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
